import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useCampaigns } from "api/campaigns";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { hasAnyAccessToResource, hasViewAccessToResource } from "api/general";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { ImportJsonModal } from "components/ImportJsonModal/ImportJsonModal";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { useTableSelectable } from "components/Table/useTableSelectable";
import { TargetTenantModal } from "components/TargetTenantModal/TargetTenantModal";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { CampaignName } from "components/Tooltips/Templates/CampaignName";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setImportedCampaigns } from "reduxState/campaigns/campaigns";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { REFERRAL_LIST_ID } from "state/tables/types";
import { CampaignTriggers, CampaignTypes } from "types/campaigns";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { backendErrors } from "utilities/backendErrors";
import { OLDate } from "utilities/dateFormat";
import { exportToJsonFile } from "utilities/exportToJson";
import { links } from "utilities/links";
import { prepareExportCampaignsJson } from "../Common/campaignHelpers";
import { CampaignItem } from "../Direct/CampaignList/CampaignList";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    errorInfo: { display: "flex", alignItems: "center", cursor: "pointer" },
    errorIcon: { width: 20, height: 20, marginLeft: theme.spacing(2) },
  }),
);

const ReferralsList = () => {
  const classes = useStyles();
  const tables = useAppSelector(selectTables);
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { listing, updateListing } = useListing(REFERRAL_LIST_ID);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const {
    rowsSelected,
    selectRow,
    unselectRow,
    selectAll,
    unselectAll,
    disabled,
    clearSelection,
  } = useTableSelectable();
  const { useCampaignsList, deleteCampaign, deleteCampaignBatch } =
    useCampaigns();
  const hasViewCampaignPermission = hasViewAccessToResource("CAMPAIGN");
  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");
  const referralsExportData = prepareExportCampaignsJson(
    rowsSelected.map((row) => row.data),
  );

  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[REFERRAL_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const [isSelectable, setIsSelectable] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    campaignId: "",
    isOpen: false,
  });
  const [isDeleteBatchModal, setDeleteBatchModal] = useState({
    isOpen: false,
    isProcessing: false,
  });
  const [isDifferentTenantModalOpen, setIsDifferentTenantModalOpen] =
    useState(false);
  const [isImportReferralsModal, setImportReferralsModal] = useState(false);
  const [errors, setErrors] = useState<any>();
  const [batchDeleteErrors, setBatchDeleteErrors] = useState<string[]>([]);

  const {
    data: referrals,
    error,
    mutate,
  } = useCampaignsList({
    filters: [
      {
        query: "type",
        condition: "eq",
        value: "referral",
      },
      ...parseFiltersForBE(getNewFiltersOnly(filters)),
    ],
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "displayOrder",
    dynamicSearch: dynamicSearch,
  });

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      REFERRAL_LIST_ID,
    );

    setFilters(filters);
  };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && referrals) {
      setPersistentFilters(filters, REFERRAL_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, referrals, error]);

  const headItems = createHeadItems([
    {
      headerText: message({ id: "referralName" }),
      property: "name",
      isSortable: true,
    },
    {
      headerText: message({ id: "status" }),
      property: "active",
      isSortable: true,
    },
    {
      headerText: message({ id: "activeFrom" }),
      property: "startsAt",
      isSortable: true,
    },
    {
      headerText: message({ id: "activeTo" }),
      property: "endsAt",
      isSortable: true,
    },
  ]);

  const createItems = (items: CampaignItem[]) =>
    items.map((item) => ({
      cells: [
        {
          content: (
            <Tooltip
              text={<CampaignName title={item.name} desc={item.description} />}
              placement="bottom-end"
            >
              <span>{item.name}</span>
            </Tooltip>
          ),
          property: "name",
          disableOverflowTooltip: true,
        },
        {
          content: <StatusValue status={item.active} />,
          property: "active",
        },
        {
          content: item.campaignActivity?.allTimeActive
            ? message({ id: "isAllTimeActive" })
            : OLDate(item.activity?.startsAt),
          property: "startsAt",
        },
        {
          content: item.campaignActivity?.allTimeActive
            ? message({ id: "isAllTimeActive" })
            : OLDate(item.activity?.endsAt),
          property: "endsAt",
        },
      ],
      id: item.campaignId,
      metadata: {
        disabled: !item.active,
        showRedemptionCodes:
          item.trigger === CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE,
      },
      data: item,
    }));

  const items = referrals?.items ? createItems(referrals.items) : [];

  const submitDeleteReferral = async (campaignId: string) => {
    try {
      const response = await deleteCampaign(campaignId);

      if (response) {
        setDeleteModal({ campaignId: "", isOpen: false });
        notify(message({ id: "referralSuccessfullyDeleted" }));
        unselectRow(campaignId);
      }
    } catch (error) {
      notify(getErrorMessage(error));
      setErrors(backendErrors(getBackendErrors(error)));
    }
  };

  const submitBulkDelete = async () => {
    setDeleteBatchModal({ isOpen: true, isProcessing: true });

    try {
      const response = await deleteCampaignBatch(rowsSelected);
      const areDeleteErrors = response.length > 0;
      await mutate();
      clearSelection();
      setIsSelectable(false);

      if (areDeleteErrors) {
        return setBatchDeleteErrors(
          response.map((campaign) => campaign.campaignName),
        );
      }
      setDeleteBatchModal({ isOpen: false, isProcessing: false });
    } catch (err) {
      notify(getErrorMessage(error));
    }
  };

  const areBatchDeleteErrors = batchDeleteErrors.length > 0;

  return (
    <>
      <TopHeader title={message({ id: "referralCampaigns" })} />
      <Subheader>
        <FiltersV2
          properties={[
            {
              query: "active",
              label: message({ id: "status" }),
              type: "boolean-status",
            },
            {
              query: "labels",
              label: message({ id: "customAttributes" }),
              type: "multiwithmodal",
            },
            {
              query: "startsAt",
              label: message({ id: "activeFrom" }),
              type: "datetime",
            },
            {
              query: "startsAt",
              label: message({ id: "activeTo" }),
              type: "datetime",
            },
            {
              query: "campaignId",
              label: message({ id: "referralId" }),
              type: "text",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "referral-name-filter",
            },
            {
              query: "trigger",
              label: message({ id: "trigger" }),
              type: "referral-trigger-filter",
            },
          ]}
          data-test-id="referral-list"
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
        />
        <SubheaderMenuButton
          text={message({ id: "addReferralCampaign" })}
          menuItems={[
            {
              text: message({ id: "addReferralCampaign" }),
              onClick: () => navigate(links.addReferral),
            },
            {
              text: message({ id: "importReferrals" }),
              onClick: () => setImportReferralsModal(true),
            },
          ]}
          disabled={hasViewCampaignPermission || !hasAnyLanguagePermission}
          data-test-id="referrals"
        />
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          {isSelectable ? (
            <>
              <SubheaderActionButton
                text={message({ id: "close" }).toUpperCase()}
                onClick={() => setIsSelectable(false)}
              />
              <SubheaderMenuButton
                text={`${message({ id: "bulkActions" }).toUpperCase()}... (${rowsSelected.length})`}
                menuItems={[
                  {
                    text: message({ id: "deleteReferrals" }),
                    onClick: () =>
                      setDeleteBatchModal({
                        isOpen: true,
                        isProcessing: false,
                      }),
                  },
                  {
                    text: message({ id: "downloadJsonFile" }),
                    onClick: () =>
                      exportToJsonFile({
                        data: referralsExportData,
                        name: "campaigns",
                        callback: () => {
                          clearSelection();
                          return setIsSelectable(false);
                        },
                      }),
                  },
                  {
                    text: message({ id: "copyToDifferentTenant" }),
                    onClick: () => setIsDifferentTenantModalOpen(true),
                  },
                ]}
                hideArrow
                disabled={rowsSelected.length === 0}
                data-test-id="table-actions"
              />
            </>
          ) : (
            <SubheaderActionButton
              text={message({ id: "bulkActions" }).toUpperCase()}
              onClick={() => setIsSelectable(true)}
              disabled={hasViewCampaignPermission}
              data-test-id="select-to-export"
            />
          )}
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: REFERRAL_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={REFERRAL_LIST_ID}
        data-test-id="referral-list"
        sort={{
          order: listing.order || "asc",
          property: listing.property || "displayOrder",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        onRowClick={(id: string) => navigate(links.referral(id as string))}
        actions={{
          body: [
            {
              action: (id) => navigate(links.editReferral(id as string)),
              disableCondition: () =>
                hasViewCampaignPermission || !hasAnyLanguagePermission,
              label: message({ id: "editReferral" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setDeleteModal({ campaignId: id as string, isOpen: true });
              },
              label: message({ id: "deleteReferral" }),
            },
            {
              action: (id) =>
                navigate(links.addReferral, {
                  state: {
                    campaignToDuplicate: id,
                  },
                }),
              disableCondition: () =>
                hasViewCampaignPermission || !hasAnyLanguagePermission,
              label: message({ id: "duplicateReferral" }),
            },
            {
              action: (id) => navigate(links.redemptionCodeList(id as string)),
              hideCondition: (item) => !item.metadata.showRedemptionCodes,
              label: message({ id: "showRedemptionCodes" }),
            },
          ],
        }}
        columns={referrals && headItems}
        rows={referrals && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(
            referrals?.total.filtered,
            referrals?.total.all,
          ),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
        selectable={
          isSelectable
            ? {
                rowsSelected,
                selectRow,
                unselectRow,
                selectAll,
                unselectAll,
                disabled,
                clearSelection,
              }
            : undefined
        }
        clearFilters={() => setFilters([])}
      />
      {deleteModal.campaignId && (
        <ConfirmationModal
          action={() => submitDeleteReferral(deleteModal.campaignId)}
          close={() => {
            setDeleteModal({ campaignId: "", isOpen: false });
            setErrors(null);
          }}
          open={deleteModal.isOpen}
          header="deleteReferralQuestion"
          info="youCantUndoThisAction"
          submitText="yesDelete"
          cancelText="cancel"
          errorInfo={errors?.["campaignId"]}
          data-test-id="delete-campaign"
          modalWidth={545}
        />
      )}
      {isDeleteBatchModal.isOpen && (
        <ConfirmationModal
          action={() => submitBulkDelete()}
          close={() => {
            setDeleteBatchModal({ isOpen: false, isProcessing: false });
            setBatchDeleteErrors([]);
          }}
          open={isDeleteBatchModal.isOpen}
          header="deleteReferralsQuestion"
          info={!areBatchDeleteErrors ? "youCantUndoThisAction" : undefined}
          submitText={"yesDelete"}
          cancelText={areBatchDeleteErrors ? "close" : "cancel"}
          errorInfo={
            areBatchDeleteErrors ? (
              <div>
                {message({ id: "deleteBatchReferralError" })}
                <p>
                  <Tooltip text={batchDeleteErrors.join(", ")}>
                    <div className={classes.errorInfo}>
                      <strong>
                        {message({ id: "referralsThatCouldNotBeRemoved" })}
                      </strong>
                      <InfoCircleIcon className={classes.errorIcon} />
                    </div>
                  </Tooltip>
                </p>
              </div>
            ) : undefined
          }
          data-test-id="delete-campaign"
          modalWidth={650}
          isSubmitDisabled={isDeleteBatchModal.isProcessing}
        />
      )}
      <TargetTenantModal
        isOpen={isDifferentTenantModalOpen}
        headerText={message(
          {
            id:
              rowsSelected.length === 1
                ? "copyOneReferralToTargetTenant"
                : "copyReferralsToTargetTenant",
          },
          {
            count: rowsSelected.length,
          },
        )}
        onClose={() => setIsDifferentTenantModalOpen(false)}
        setJsonAction={setImportedCampaigns}
        data={referralsExportData}
        link={links.referralsImportList}
        infoText={message({ id: "copiedReferralsInfo" })}
        buttonText={message({
          id:
            rowsSelected.length === 1
              ? "copyReferralAndViewTargetTenant"
              : "copyReferralsAndViewTargetTenant",
        })}
      />
      <ImportJsonModal
        headerMessage="importReferrals"
        isOpen={isImportReferralsModal}
        onClose={() => setImportReferralsModal(false)}
        importTooBigErrorMessage="maxImportReferralCountReached"
        setJsonAction={setImportedCampaigns}
        link={links.referralsImportList}
        validate={(campaigns: any[]) => {
          return campaigns.length === 0 ? "fileCannotByImported" : null;
        }}
        filter={(campaigns: any[]) =>
          campaigns.filter(
            (campaign) => campaign.type === CampaignTypes.REFERRAL,
          )
        }
      />
    </>
  );
};

export default ReferralsList;
